import { components } from "../api/v1";

type ServiceSession = components["schemas"]["ServiceSession"];

export type ConsoleSessionState =
  | "loading"
  | "ready_no_session"
  | "active"
  | "awaiting_closeout"
  | "active_elsewhere"
  | "incomplete_session"
  | "unverified"
  | "submitting";

export interface ConsoleSessionView {
  state: ConsoleSessionState;
  session: ServiceSession | null;
  activeSession: ServiceSession | null;
  elapsedSeconds: number | null;
  startedAtMs: number | null;
  message: string | null;
}

export function parseSessionTimestamp(value: unknown): number | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

export function getSafeDurationSeconds(startedAt: unknown, endedAt: unknown): number | null {
  const startedAtMs = parseSessionTimestamp(startedAt);
  const endedAtMs = parseSessionTimestamp(endedAt);
  if (startedAtMs === null || endedAtMs === null || endedAtMs < startedAtMs) return null;
  return Math.floor((endedAtMs - startedAtMs) / 1000);
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asServiceSession(value: unknown): ServiceSession | null {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.ticketId !== "string" || typeof value.status !== "string") return null;
  return value as ServiceSession;
}

function asServiceSessionList(value: unknown): ServiceSession[] {
  return Array.isArray(value) ? value.map(asServiceSession).filter((item): item is ServiceSession => item !== null) : [];
}

export function deriveConsoleSessionView(ticketId: string, context: unknown): ConsoleSessionView {
  if (!isRecord(context)) return { state: "unverified", session: null, activeSession: null, elapsedSeconds: null, startedAtMs: null, message: "Session status could not be verified. Retry sync before starting or closing a visit." };

  const activeSession = asServiceSession(context.activeSession);
  const pendingCloseouts = asServiceSessionList(context.pendingCloseouts);
  const serverTimeMs = parseSessionTimestamp(context.serverTime);
  const session = activeSession?.ticketId === ticketId ? activeSession : pendingCloseouts.find((item) => item.ticketId === ticketId) ?? null;
  if (!session) {
    if (activeSession) return { state: "active_elsewhere", session: null, activeSession, elapsedSeconds: null, startedAtMs: null, message: "A service visit is active on another ticket. Switch to it or finish it before starting another visit." };
    return { state: serverTimeMs === null ? "unverified" : "ready_no_session", session: null, activeSession, elapsedSeconds: null, startedAtMs: null, message: serverTimeMs === null ? "Session clock could not be verified. Retry sync before starting a timed visit." : null };
  }
  if (session.status === "active") {
    const startedAtMs = parseSessionTimestamp(session.startedAt);
    if (startedAtMs === null) return { state: "incomplete_session", session, activeSession, elapsedSeconds: null, startedAtMs: null, message: "This active visit is missing a valid start time. Service time and closeout are locked until the session is repaired on the server." };
    if (serverTimeMs === null) return { state: "unverified", session, activeSession, elapsedSeconds: null, startedAtMs, message: "The active visit was found, but its server clock could not be verified. Retry sync before changing service time." };
    return { state: "active", session, activeSession, elapsedSeconds: Math.max(0, Math.floor((serverTimeMs - startedAtMs) / 1000)), startedAtMs, message: null };
  }
  if (session.status === "awaiting_closeout") {
    const elapsedSeconds = getSafeDurationSeconds(session.startedAt, session.endedAt);
    if (elapsedSeconds === null) return { state: "incomplete_session", session, activeSession, elapsedSeconds: null, startedAtMs: parseSessionTimestamp(session.startedAt), message: "This finished visit has incomplete time information. Closeout is locked until the session is repaired on the server." };
    return { state: "awaiting_closeout", session, activeSession, elapsedSeconds, startedAtMs: parseSessionTimestamp(session.startedAt), message: null };
  }
  return { state: "incomplete_session", session, activeSession, elapsedSeconds: null, startedAtMs: null, message: "This visit has an unsupported status and cannot be changed safely. Retry sync or return to tickets." };
}
